import axios from "axios";
import { jwtDecode } from "jwt-decode";
import AuthService from "./authentication.service";

/**
 * Access tokens last 2 hours; the refresh token (7 days, single use) buys a
 * new pair from POST /api/Account/refreshToken.
 *
 * Two rules drive everything here:
 *  - Only one refresh may be in flight for a given refresh token. A second
 *    call with the same token fails, and a reused token can make the server
 *    revoke the whole session. Inside a tab, callers share one promise; across
 *    tabs, a Web Lock serialises the call and each tab re-reads localStorage
 *    once it holds the lock, so a token another tab just refreshed is reused.
 *  - Any non-success response from the refresh endpoint (it answers 400, not
 *    401) means the session is over.
 */

const { REACT_APP_API_BASE_URL } = process.env;

const REFRESH_PATH = "/api/Account/refreshToken";
const LOCK_NAME = "tpm-auth-token-refresh";
// Refresh this long before the access token expires, so the first request
// after expiry does not have to fail with a 401 first.
const PROACTIVE_LEAD_MS = 90 * 1000;
// setTimeout overflows above ~24.8 days.
const MAX_TIMEOUT_MS = 2147483647;

// Endpoints that must never trigger a refresh, or a failed refresh would loop.
const AUTH_ENDPOINT = /\/account\/(login|confirmcode|refreshtoken|logout)\b/i;
export const isAuthEndpoint = (url = "") => AUTH_ENDPOINT.test(url);

// No interceptors on purpose: a failing refresh must not trigger another
// refresh, the 401 logout, or the 400 subscription modal.
const refreshClient = axios.create({
  baseURL: REACT_APP_API_BASE_URL,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
});

const sessionEndedError = () => {
  const error = new Error("Session expired");
  error.sessionEnded = true;
  return error;
};

// Milliseconds since epoch, or null when the token is missing or not a JWT.
export const tokenExpiresAt = (token) => {
  if (!token) return null;
  try {
    const { exp } = jwtDecode(token);
    return exp ? exp * 1000 : null;
  } catch (e) {
    return null;
  }
};

export const isAccessTokenExpired = (token) => {
  const expiresAt = tokenExpiresAt(token);
  return expiresAt !== null && expiresAt <= Date.now();
};

const withCrossTabLock = (task) =>
  typeof navigator !== "undefined" && navigator.locks?.request
    ? navigator.locks.request(LOCK_NAME, task)
    : task();

let inFlight = null;

/**
 * Returns a valid access token, refreshing if `staleToken` is still the one
 * in storage. Pass the token the failed request was sent with.
 *
 * Rejects with `error.sessionEnded === true` when the server refused the
 * refresh (local auth state is cleared by then); rejects with the network
 * error otherwise, leaving the session alone.
 */
export const refreshAccessToken = (staleToken) => {
  if (!inFlight) {
    inFlight = withCrossTabLock(async () => {
      // Another request in this tab, or another tab, may have refreshed while
      // we waited for the lock.
      const current = AuthService.getAccessToken();
      if (current && current !== staleToken) return current;

      const refreshToken = AuthService.getRefreshToken();
      if (!refreshToken) throw sessionEndedError();

      let response;
      try {
        response = await refreshClient.post(REFRESH_PATH, { refreshToken });
      } catch (error) {
        if (!error?.response) throw error;
        AuthService.clearSession();
        throw sessionEndedError();
      }

      const user = response?.data?.data;
      if (!user?.token || !user?.refreshToken) {
        AuthService.clearSession();
        throw sessionEndedError();
      }

      AuthService.setSession(user);
      return user.token;
    }).finally(() => {
      inFlight = null;
      scheduleProactiveRefresh();
    });
  }
  return inFlight;
};

/** A token that is safe to send right now, refreshing first if it expired. */
export const getFreshAccessToken = async () => {
  const token = AuthService.getAccessToken();
  if (!token || !AuthService.getRefreshToken() || !isAccessTokenExpired(token))
    return token;
  try {
    return await refreshAccessToken(token);
  } catch (e) {
    return AuthService.getAccessToken();
  }
};

let proactiveTimer = null;

export const scheduleProactiveRefresh = () => {
  clearTimeout(proactiveTimer);
  proactiveTimer = null;

  const token = AuthService.getAccessToken();
  const expiresAt = tokenExpiresAt(token);
  if (!expiresAt || !AuthService.getRefreshToken()) return;

  const delay = Math.min(
    Math.max(expiresAt - Date.now() - PROACTIVE_LEAD_MS, 0),
    MAX_TIMEOUT_MS
  );
  proactiveTimer = setTimeout(() => {
    refreshAccessToken(token).catch((error) => {
      if (error?.sessionEnded) AuthService.endSession();
    });
  }, delay);
};

/** Saves the user object from login / confirmCode and starts the timer. */
export const startSession = (user) => {
  AuthService.setSession(user);
  scheduleProactiveRefresh();
};

let initialised = false;

export const initTokenRefresh = () => {
  if (initialised) return;
  initialised = true;
  scheduleProactiveRefresh();
  // Fires only for changes made by other tabs: re-arm the timer for the token
  // they stored (or stop it after they logged out).
  window.addEventListener("storage", (event) => {
    if (event.key === null || event.key === "token") {
      scheduleProactiveRefresh();
    }
  });
};
